import React, { Component } from 'react';
import { filter, indexOf } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { Field, reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import Joi from 'joi';
import {
  Button, Modal, FormGroup,
  Carousel, ControlLabel, HelpBlock,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { connect } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';
import { createTVFieldMapping } from '../../SetupActions';
import GroupRowInnerRenderer from './GroupRowInnerRenderer';
import TVFieldNameRenderer from './TVFieldNameRenderer';
import TVFieldTypeRenderer from './TVFieldTypeRenderer';
import SearchField from '../../../../components/search-field';
import SearchPath from '../../../../components/search-path';
import { convertTSFieldTypeToTVFieldTypeNum } from '../../../../util/truvoice';

// Legacy constraints for reference:
// const constraints = {
//   programId: {
//     presence: { allowEmpty: false },
//   },
//   clientId: {
//     presence: { allowEmpty: false },
//   },
//   tvFieldId: {
//     presence: { allowEmpty: false },
//   },
//   search_paths: {
//     presence: { allowEmpty: false },
//   },
// };

const schema = Joi.object({
  programId: Joi.string().required().messages({
    'string.base': 'Program ID must be a string',
    'string.empty': 'Program ID is required',
    'any.required': 'Program ID is required',
  }),
  clientId: Joi.string().required().messages({
    'string.base': 'Client ID must be a string',
    'string.empty': 'Client ID is required',
    'any.required': 'Client ID is required',
  }),
  tvFieldId: Joi.string().required().messages({
    'string.base': 'TruVoice Field ID must be a string',
    'string.empty': 'TruVoice Field ID is required',
    'any.required': 'TruVoice Field ID is required',
  }),
  search_paths: Joi.array().min(1).required().messages({
    'array.base': 'Search paths must be an array',
    'array.min': 'At least one search path is required',
    'any.required': 'Search paths are required',
  }),
}).unknown(true);

const validateMapping = (v) => {
  const { error } = schema.validate(v, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};

class AddModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      totalWith: 0,
      selectedTVField: null,
      selectedSearchPaths: [],
      step: 0,
      validFields: [],
      tsFieldType: null,
      showNoMatchFieldError: false,
    };
    this.hideModal = this.hideModal.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.goNext = this.goNext.bind(this);
    this.goBack = this.goBack.bind(this);
    this.updateSearchPath = this.updateSearchPath.bind(this);
    this.renderTruSalesField = this.renderTruSalesField.bind(this);
    this.renderTruVoiceField = this.renderTruVoiceField.bind(this);
  }


  updateSearchPath(paths, field) {
    if (field) {
      const { type } = field;
      const validTVTypes = convertTSFieldTypeToTVFieldTypeNum(type);
      // const validFields = filter(this.props.tvFields, { dataType: validTVType });
      const validFields = filter(this.props.tvFields, (f) => {
        return indexOf(validTVTypes, f.dataType) >= 0;
      });
      if (validFields.length === 0) {
        this.setState({ selectedSearchPaths: [], validFields, tsFieldType: type, showNoMatchFieldError: true }, () => {
          this.props.change('search_paths', []);
        });

      } else {
        // has valid field
        this.setState({ selectedSearchPaths: paths, validFields, tsFieldType: type, showNoMatchFieldError: false }, () => {
          this.props.change('search_paths', paths);
          this.goNext();
        });
      }
    } else {
      this.setState({ selectedSearchPaths: [], validFields: [], tsFieldType: null, showNoMatchFieldError: false }, () => {
        this.props.change('search_paths', []);
      });
    }
    
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  hideModal() {
    this.props.onHide();
    this.setState({ selectedTVField: null, step: 0, selectedSearchPaths: [], tsFieldType: null, validFields: [], showNoMatchFieldError: false });
    this.props.initialize({
      clientId: null,
      programId: null,
      tvFieldId: null,
      search_paths: [],
    });
  }

  goNext() {
    this.setState({ step: 1, selectedSearchPaths: [] });
  }
  
  goBack() {
    this.setState({ step: 0, selectedSearchPaths: [] });
  }

  submit(data) {
    const { source } = this.props;
    const p = this.props.dispatch(createTVFieldMapping(source, data));
    return p.then(
      (results) => {
        this.hideModal();
        this.props.onAdded(results);
      },
      (errors) => {
        const e = errors.data || errors;
        if (e.message) {
          e._error = e.message;
        }
        throw new SubmissionError(e);
      },
    );
  }

  onSelectionChanged() {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows && selectedRows.length > 0) {
      this.props.change('clientId', selectedRows[0].client_id);
      this.props.change('programId', selectedRows[0].program_id);
      this.props.change('tvFieldId', selectedRows[0].id);
      this.setState({ selectedTVField: selectedRows[0] });
    } else {
      this.props.change('clientId', null);
      this.props.change('programId', null);
      this.props.change('tvFieldId', null);
      this.setState({ selectedTVField: null });
    }
  }

  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  onResizNavBar(totalWith) {
    this.setState({ totalWith });
  }

  renderTruVoiceField({
    label, meta: { touched, error }, validFields, totalWith
  }) {
    const { clients, programs, multiClients } = this.props;
    const mainWidth = 300;
    if (validFields.length === 0) {
      return this.renderNoMatchError();
    }
    const columnDefs = [
      ...(multiClients ? [
        {
          headerName: 'Client Id',
          field: 'client_id',
          suppressMenu: true,
          rowGroup: true,
          rowGroupIndex: 0,
          hide: true,
          cellRendererParams: {},
        },
        {
          headerName: 'Client Name',
          field: 'client_name',
          suppressMenu: true,
          hide: true,
        },
      ] : []),
      {
        headerName: 'Program Id',
        field: 'program_id',
        suppressMenu: true,
        rowGroup: true,
        rowGroupIndex: multiClients ? 1 : 0,
        hide: true,
        cellRendererParams: {},
      },
      {
        headerName: 'Program Name',
        field: 'program_name',
        suppressMenu: true,
        hide: true,
      },
      {
        headerName: 'Parent Object',
        field: 'parent',
        hide: true,
        rowGroupIndex: multiClients ? 2 : 1,
        cellRendererParams: {},
      },
      {
        headerName: 'TruVoice Field',
        field: 'name',
        cellRendererParams: {},
        cellRenderer: TVFieldNameRenderer,
        pinned: 'left',
        width: mainWidth,
        suppressMenu: true,
        checkboxSelection: true,
      },
      {
        headerName: 'Data Type',
        field: 'dataName',
        cellRenderer: TVFieldTypeRenderer,
        cellRendererParams: {},
        suppressMenu: true,
        width: totalWith - mainWidth - 2,
      },
    ];
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState}>
        {label && label !== '' && <ControlLabel>{label}</ControlLabel>}
        <nav className="navbar navbar-default navbar-controls">
          <div className="navbar-form">
            <SearchField onSearch={this.handleSearch} />
          </div>
          <ReactResizeDetector handleWidth onResize={this.onResizNavBar} />
        </nav>
        <div className={`ag-theme-alpine`} style={{ height: '400px', width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            groupUseEntireRow
            groupDisplayType="groupRows"
            groupDefaultExpanded={-1}
            groupRowRendererParams={{
              innerRenderer: GroupRowInnerRenderer,
            }}
            defaultColDef={{
              resizable: true,
            }}
            columnDefs={columnDefs}
            getRowNodeId={(d) => d.id}
            onGridReady={this.onGridReady}
            rowData={validFields}
            rowSelection="single"
            checkboxSelection
            context={{
              clients,
              programs,
            }}
            onSelectionChanged={this.onSelectionChanged}
          />
        </div>
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderNoMatchError() {
    const { validFields, tsFieldType } = this.state;
    if (validFields.length === 0) {
      return (
        <div className="alert alert-warning">
          There are no compatible TruVoice fields that match the selected CRM field type <code>{tsFieldType}</code>
        </div>
      );
    }
    return null;
  }

  renderTruSalesField({
    label, meta: { touched, error },
  }) {
    const { submitting, source } = this.props;
    const validationState = touched && error ? 'error' : null;
    const { selectedSearchPaths, showNoMatchFieldError } = this.state;
    return (
      <FormGroup validationState={validationState}>
        {label && label !== '' && <ControlLabel>{label}</ControlLabel>}
        {showNoMatchFieldError && this.renderNoMatchError()}
        <SearchPath
          paths={selectedSearchPaths}
          disabled={submitting}
          onUpdate={this.updateSearchPath}
          selectedSearchPaths={selectedSearchPaths}
          checkFieldType
          startingNode={source === 'account' ? 'Account' : 'Opportunity'}
        />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    )
  }

  render() {
    const { handleSubmit, valid, submitting, error } = this.props;
    const { step, selectedSearchPaths, validFields, totalWith } = this.state;
    const direction = step === 1 ? 'next' : 'prev';
    const disabledNext = !selectedSearchPaths || selectedSearchPaths.length === 0;

    return (
      <Modal key="modal" show={this.props.show} onHide={this.hideModal}>
        <form
          onSubmit={handleSubmit(this.submit)}
        >
          <Modal.Header closeButton>
            <h4>Add New TruVoice Mapping</h4>
          </Modal.Header>
          <Modal.Body>
            {error && error.length > 0 &&
              <div className="alert alert-danger">
                {error}
              </div>
            }
            <Carousel
              activeIndex={step}
              indicators={false}
              interval={null}
              controls={false}
              direction={direction}
            >
              <Carousel.Item>
                <Field
                  name="search_paths"
                  type="text"
                  component={this.renderTruSalesField}
                  label="Please select a CRM Field:"
                />
              </Carousel.Item>
              <Carousel.Item>
                <Field
                  name="tvFieldId"
                  type="text"
                  component={this.renderTruVoiceField}
                  validFields={validFields}
                  totalWith={totalWith}
                  label="Please select a TruVoice Field:"
                />
              </Carousel.Item>
            </Carousel>
          </Modal.Body>
          <Modal.Footer className="clearfix">
            <Button className="pull-left" onClick={this.hideModal}>Cancel</Button>
            {step === 0 && <Button className="pull-right" disabled={(disabledNext || submitting)} bsStyle="primary" onClick={this.goNext}>Next <Fa name="chevron-right" /></Button>}
            {step === 1 && <Button className="pull-right" disabled={(!valid || submitting)} type="submit" bsStyle="primary">Submit</Button>}
            {' '}
            {step === 1 && <Button className="pull-right" disabled={submitting} onClick={this.goBack}><Fa name="chevron-left" /> Back</Button>}
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

// Retrieve data from store as props
const selector = formValueSelector('addTVMappingForm'); // <-- same as form name

function mapStateToProps() {
  return {
  };
}

AddModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onHide: PropTypes.func,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  valid: PropTypes.bool,
  resetForm: PropTypes.func,
  invalid: PropTypes.bool,
  change: PropTypes.func,
  error: PropTypes.string,
  onAdded: PropTypes.func,
  tvFields: PropTypes.arrayOf(PropTypes.shape({})),
  clients: PropTypes.shape({}),
  programs: PropTypes.shape({}),
  multiClients: PropTypes.bool,
  source: PropTypes.string.isRequired,
};

AddModal.defaultProps = {
  show: false,
  submitting: false,
  handleSubmit: () => {},
  initialize: () => {},
  resetForm: () => {},
  change: () => {},
  invalid: false,
  valid: false,
  error: null,
  onAdded: () => {},
  onHide: () => {},
  tvFields: [],
  clients: {},
  programs: {},
  multiClients: false,
};

const AddForm = reduxForm({
  form: 'addTVMappingForm',
  validate: validateMapping,
})(AddModal);


export default connect(mapStateToProps)(AddForm);
